
import React, { useState, useEffect } from "react";

import {
  Box,
  Button,
  Divider,
  Popover,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";

import AccessTimeIcon from "@material-ui/icons/AccessTime";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";

import { COLORS } from "./logsConstants";

const PRESETS = [
  { label: "Last 5 min", value: 5 },
  { label: "Last 30 min", value: 30 },
  { label: "Last 1 hour", value: 60 },
];

const DEFAULT_TIME = "all";

const useStyles = makeStyles((theme) => ({
  trigger: {
    fontSize: "12.5px",
    fontWeight: 400,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: 6,
    backgroundColor: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    padding: "5px 8px",
    color: COLORS.textPrimary,
    textTransform: "none",
    minWidth: 0,
    lineHeight: 1.5,
    boxShadow: "none",
    whiteSpace: "nowrap",

    "&:hover": {
      backgroundColor: "#fff",
      borderColor: COLORS.borderStrong,
      boxShadow: "none",
    },

    "&:focus": {
      backgroundColor: "#fff",
    },

    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
  },

  triggerIcon: {
    fontSize: 15,
    color: COLORS.textPrimary,
    flexShrink: 0,
  },

  triggerLabel: {
    fontSize: "12.5px",
    fontWeight: 400,
    lineHeight: 1.5,
    whiteSpace: "nowrap",
  },

  panel: {
    width: 570,
    maxWidth: "calc(100vw - 32px)",
    padding: theme.spacing(1.5),
    boxSizing: "border-box",
  },

  presets: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },

  preset: {
    minWidth: 0,
    padding: "5px 12px",
    fontSize: 12,
    textTransform: "none",
    borderColor: theme.palette.divider,
    whiteSpace: "nowrap",
  },

  selectedPreset: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,

    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },

  customTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
  },

  fields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),

    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },

  fieldLabel: {
    display: "block",
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },

  input: {
    minWidth: 0,

    "& .MuiInputBase-root": {
      height: 36,
      fontSize: 13,
    },

    "& input": {
      padding: "8px 10px",
      minWidth: 0,
    },
  },

  dateTimeFields: {
    display: "flex",
    gap: 8,
    minWidth: 0,

    "& > *": {
      flex: 1,
      width: "50%",
      minWidth: 0,
    },
  },

  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },

  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },

  footerButton: {
    textTransform: "none",
  },

  error: {
    color: theme.palette.error.main,
    fontSize: 12,
    marginTop: theme.spacing(1),
  },
}));

function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toTimeInput(date) {
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
}

function getInitialDateRange() {
  const now = new Date();
  const earlier = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  return {
    fromDate: toDateInput(earlier),
    toDate: toDateInput(now),
    fromTime: "00:00",
    toTime: "23:59",
  };
}

function formatDateTime(date) {
  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function getLabel(value) {
  if (value == null || value === "all") {
    return "All time";
  }

  if (typeof value === "number") {
    const preset = PRESETS.find((item) => item.value === value);

    return preset ? preset.label : `Last ${value} min`;
  }

  if (
    value.type === "custom" &&
    value.start != null &&
    value.end != null
  ) {
    return `${formatDateTime(value.start)} – ${formatDateTime(
      value.end
    )}`;
  }

  return "All time";
}

/**
 * value:
 *   "all"
 *   5 | 30 | 60
 *   { type: "custom", start: Date, end: Date }
 *
 * onChange receives "all", a preset number, or a custom range object.
 *
 * The parent component must interpret "all" as no time restriction.
 */
export default function LogTimeFilter({
  value = DEFAULT_TIME,
  onChange,
  onReset,
}) {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [range, setRange] = useState(getInitialDateRange);
  const [error, setError] = useState("");

  const open = Boolean(anchorEl);
  const isCustom = value?.type === "custom";

  useEffect(() => {
    if (!isCustom || !value.start || !value.end) {
      return;
    }

    const start = new Date(value.start);
    const end = new Date(value.end);

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime())
    ) {
      return;
    }

    setRange({
      fromDate: toDateInput(start),
      toDate: toDateInput(end),
      fromTime: toTimeInput(start),
      toTime: toTimeInput(end),
    });
  }, [isCustom, value]);

  const handleOpen = (event) => {
    setError("");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setError("");
  };

  const updateRange = (key) => (event) => {
    setRange((previous) => ({
      ...previous,
      [key]: event.target.value,
    }));

    setError("");
  };

  const handlePreset = (minutes) => {
    if (onChange) {
      onChange(minutes);
    }

    handleClose();
  };

  const handleAllTime = () => {
    if (onChange) {
      onChange("all");
    }

    handleClose();
  };

  const handleReset = () => {
    setRange(getInitialDateRange());
    setError("");

    if (onChange) {
      onChange(DEFAULT_TIME);
    }

    if (onReset) {
      onReset();
    }

    handleClose();
  };

  const handleApply = () => {
    const { fromDate, toDate, fromTime, toTime } = range;

    if (!fromDate || !toDate || !fromTime || !toTime) {
      setError("Please enter both dates and times.");
      return;
    }

    const start = new Date(`${fromDate}T${fromTime}:00`);
    const end = new Date(`${toDate}T${toTime}:59`);

    if (
      Number.isNaN(start.getTime()) ||
      Number.isNaN(end.getTime()) ||
      start > end
    ) {
      setError("The start date and time must be before the end.");
      return;
    }

    if (onChange) {
      onChange({
        type: "custom",
        start,
        end,
      });
    }

    handleClose();
  };

  const selectedPreset =
    typeof value === "number" ? value : null;

  return (
    <>
      <Button
        className={classes.trigger}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Time filter: ${getLabel(value)}`}
      >
        <AccessTimeIcon className={classes.triggerIcon} />

        <Typography className={classes.triggerLabel}>
          {getLabel(value)}
        </Typography>

        <ExpandMoreIcon className={classes.triggerIcon} />
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        id="date-filter"
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            marginTop: 4,
            borderRadius: 6,
            overflow: "hidden",
          },
        }}
      >
        <Box className={classes.panel}>
          <Box className={classes.presets}>
            <Button
              variant="outlined"
              className={`${classes.preset} ${
                value === "all" || value == null
                  ? classes.selectedPreset
                  : ""
              }`}
              onClick={handleAllTime}
            >
              All time
            </Button>

            {PRESETS.map((preset) => (
              <Button
                key={preset.value}
                variant="outlined"
                className={`${classes.preset} ${
                  selectedPreset === preset.value
                    ? classes.selectedPreset
                    : ""
                }`}
                onClick={() => handlePreset(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          <Divider style={{ margin: "12px 0" }} />

          <Typography className={classes.customTitle}>
            Custom range
          </Typography>

          <Box className={classes.fields}>
            <Box>
              <Typography className={classes.fieldLabel}>
                From
              </Typography>

              <Box className={classes.dateTimeFields}>
                <TextField
                  className={classes.input}
                  variant="outlined"
                  type="date"
                  value={range.fromDate}
                  onChange={updateRange("fromDate")}
                  inputProps={{ "aria-label": "Start date" }}
                  fullWidth
                />

                <TextField
                  className={classes.input}
                  variant="outlined"
                  type="time"
                  value={range.fromTime}
                  onChange={updateRange("fromTime")}
                  inputProps={{ "aria-label": "Start time" }}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Typography className={classes.fieldLabel}>
                To
              </Typography>

              <Box className={classes.dateTimeFields}>
                <TextField
                  className={classes.input}
                  variant="outlined"
                  type="date"
                  value={range.toDate}
                  onChange={updateRange("toDate")}
                  inputProps={{ "aria-label": "End date" }}
                  fullWidth
                />

                <TextField
                  className={classes.input}
                  variant="outlined"
                  type="time"
                  value={range.toTime}
                  onChange={updateRange("toTime")}
                  inputProps={{ "aria-label": "End time" }}
                  fullWidth
                />
              </Box>
            </Box>
          </Box>

          {error && (
            <Typography className={classes.error} role="alert">
              {error}
            </Typography>
          )}

          <Box className={classes.footer}>
            <Button
              size="small"
              className={classes.footerButton}
              startIcon={<RefreshIcon />}
              onClick={handleReset}
            >
              Reset
            </Button>

            <Box className={classes.actions}>
              <Button
                size="small"
                className={classes.footerButton}
                onClick={handleClose}
              >
                Cancel
              </Button>

              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.footerButton}
                onClick={handleApply}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
  